import { useState } from "react";
import { Landmark, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { today } from "@/lib/format";
import { Field, Money, NativeSelect } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function CtPayments({ data, onChange }) {
  const { t, locale } = useLang();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ date: today(), amount: "", account_code: "1100", reference: "" });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/reports/corporate-tax/payment", { ...f, amount: Number(f.amount) });
      onChange(r.data);
      setOpen(false);
      toast.success(t("ctPaymentDone"));
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div className="mt-4 rounded-lg border border-slate-200 p-4 text-sm" data-testid="ct-payments-box">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-semibold text-slate-900"><Landmark className="h-4 w-4 text-[#D4AF37]" />{t("ctPayments")}</div>
          <div className="mt-1 text-xs text-slate-600">{t("ctPayableBalance")}: <Money testid="ct-payable-balance" value={data.payable_balance} className={`font-semibold ${data.payable_balance > 0 ? "text-[#CE1126]" : "text-[#007A3D]"}`} /></div>
        </div>
        <Button data-testid="ct-payment-btn" size="sm" onClick={() => { setF({ ...f, amount: data.payable_balance > 0 ? String(data.payable_balance) : "" }); setOpen(true); }} className="bg-[#0B1220] text-white hover:bg-slate-800">{t("ctRecordPayment")}</Button>
      </div>
      {data.payments.length > 0 && (
        <table className="data-table compact mt-3"><thead><tr><th>{t("date")}</th><th>{t("reference")}</th><th className="text-end">{t("amount")}</th></tr></thead>
          <tbody>{data.payments.map((p) => <tr key={p.id} data-testid={`ct-payment-row-${p.id}`}><td className="font-mono">{p.date}</td><td className="text-slate-600">{p.reference}</td><td className="text-end"><Money value={p.amount} /></td></tr>)}</tbody></table>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ct-payment-dialog">
          <DialogHeader><DialogTitle>{t("ctRecordPayment")}</DialogTitle><DialogDescription>{t("ctRecordPaymentText")}</DialogDescription></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <Field label={t("date")}><Input data-testid="ct-payment-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} required /></Field>
            <Field label={`${t("amount")} (AED)`}><Input data-testid="ct-payment-amount" type="number" min="0.01" step="0.01" className="font-mono" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required /></Field>
            <Field label={t("paidFrom")}>
              <NativeSelect data-testid="ct-payment-account" value={f.account_code} onChange={(e) => setF({ ...f, account_code: e.target.value })}>
                <option value="1100">1100 — {t("bank")}</option><option value="1000">1000 — {t("cash")}</option>
              </NativeSelect>
            </Field>
            <Field label={t("reference")}><Input data-testid="ct-payment-ref" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="EmaraTax ref." /></Field>
            <Button data-testid="ct-payment-submit" disabled={busy} className="w-full bg-[#007A3D] hover:bg-[#005E2E]">{busy && <Loader2 className="me-2 h-4 w-4 animate-spin" />}{t("save")}</Button>
          </form>
        </DialogContent>
      </Dialog>
      <p className="mt-2 text-[11px] text-slate-400">{new Date().toLocaleDateString(locale)} · {t("ctPaymentNote")}</p>
    </div>
  );
}
